import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  useTheme,
  useMediaQuery,
  Box,
  Button,
  Menu,
  MenuItem,
  MenuList,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Badge,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useGetUserByEmailQuery } from "../../services/auth.query";
import { User } from "@supabase/supabase-js";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { signOut } from "../../services/AuthService";
import { useNavigate } from "react-router";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import {
  useGetAdminNotificationsQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
} from "../../services/notification.query";

function Header({ handleDrawerToggle, collapsed }: any) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const lUser: User = JSON.parse(localStorage.getItem("user") as string);
  const email = lUser?.email;
  const { data: userInfo, isLoading } = useGetUserByEmailQuery(email);
  const navigator = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleLogout = async () => {
    setLoading(true);
    try {
      await signOut();
      navigator("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
    setLoading(false);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleProfileMenu = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const drawerWidth = collapsed ? 80 : 260;

  // Notifications
  const { data: notifications = [] } = useGetAdminNotificationsQuery(lUser?.id ? Number(lUser.id) : 0, {
    skip: !lUser?.id,
    pollingInterval: 30000,
  });

  const unreadCount = notifications.filter((n: any) => !n.is_read).length;

  const [markAsRead] = useMarkNotificationAsReadMutation();
  const [markAllAsRead] = useMarkAllNotificationsAsReadMutation();

  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const openNotif = Boolean(notifAnchorEl);

  const handleNotifMenu = (event: React.MouseEvent<HTMLElement>) => {
    setNotifAnchorEl(event.currentTarget);
  };

  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const handleNotificationClick = async (notif: any) => {
    if (!notif.is_read) {
      await markAsRead(notif.id);
    }
    handleNotifClose();

    if (notif.type === "APP_ISSUE") navigator("/appissues");
    else if (notif.type === "NEW_CINEMA") navigator("/movie-house/theaters");
    else if (notif.type === "MISSING_POSTER") navigator("/movies/movies");
    else if (notif.type === "VIOLATION_MOVIE") navigator("/deputies/reviews");
    else if (notif.type === "VIOLATION_TV") navigator("/deputies/tv-reports");
    else if (notif.type === "VIOLATION_OTHER") navigator("/deputies/other-reports");
  };

  return (
    <>
      <AppBar
        elevation={0}
        position="fixed"
        sx={{
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e5e7eb",
          color: "#374151",
          zIndex: (theme) => theme.zIndex.drawer - 1, 
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          height: 70,
          px: isMobile ? 2 : 4,
          justifyContent: "center",
          transition: "width 0.3s ease-in-out, margin-left 0.3s ease-in-out",
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", paddingLeft: "0 !important" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* Hamburger menu - useful mostly for mobile, but DJobsite has it */}
            <IconButton
              color="inherit"
              aria-label="toggle drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }} // Removed display: 'none' for md screens
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Right Side Actions */}
          <Box display="flex" alignItems="center" gap={2}>
            {/* Notifications */}
            <IconButton color="inherit" onClick={handleNotifMenu}>
              <Badge badgeContent={unreadCount} color="error" variant={unreadCount > 0 ? "standard" : "dot"} invisible={unreadCount === 0 && notifications.length === 0}>
                <NotificationsNoneOutlinedIcon />
              </Badge>
            </IconButton>

            <Menu
              anchorEl={notifAnchorEl}
              open={openNotif}
              onClose={handleNotifClose}
              PaperProps={{
                elevation: 3,
                sx: { mt: 1.5, borderRadius: 2, minWidth: 320, maxWidth: 360, maxHeight: 400 },
              }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle2" fontWeight={600} color="#111827">
                  Notifications
                </Typography>
                {unreadCount > 0 && (
                  <Button size="small" onClick={() => markAllAsRead(Number(lUser?.id))} sx={{ textTransform: "none", fontSize: 12 }}>
                    Mark all read
                  </Button>
                )}
              </Box>
              <MenuList sx={{ py: 0, maxHeight: 300, overflowY: "auto" }}>
                {notifications.length === 0 ? (
                  <MenuItem disabled>
                    <ListItemText primary="No notifications" />
                  </MenuItem>
                ) : (
                  notifications.map((notif: any) => (
                    <MenuItem
                      key={notif.id}
                      onClick={() => handleNotificationClick(notif)}
                      sx={{
                        py: 1.5,
                        borderBottom: "1px solid #f3f4f6",
                        backgroundColor: notif.is_read ? "transparent" : "#f0f9ff",
                        "&:hover": { backgroundColor: notif.is_read ? "#f9fafb" : "#e0f2fe" },
                      }}
                    >
                      <ListItemText
                        primary={notif.title}
                        secondary={
                          <>
                            <Typography variant="caption" display="block" color="textSecondary" sx={{ mb: 0.5, whiteSpace: "normal" }}>
                              {notif.message}
                            </Typography>
                            <Typography variant="caption" color="primary">
                              {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                            </Typography>
                          </>
                        }
                        primaryTypographyProps={{ fontSize: 13, fontWeight: notif.is_read ? 500 : 600, color: "#111827" }}
                      />
                    </MenuItem>
                  ))
                )}
              </MenuList>
            </Menu>

            {/* Profile Dropdown */}
            <Button
              id="basic-button"
              aria-controls={open ? "basic-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={handleProfileMenu}
              sx={{
                display: "flex",
                alignItems: "center",
                p: "4px 8px",
                textTransform: 'none',
                borderRadius: 2,
                "&:hover": { backgroundColor: "#f3f4f6" }
              }}
            >
              <Avatar
                src={userInfo?.profile_picture}
                alt={userInfo?.name}
                sx={{ width: 40, height: 40 }}
              />
              {!isMobile && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "start", ml: 1.5 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ textTransform: "capitalize", color: "#111827", fontWeight: 600, lineHeight: 1.2 }}
                  >
                    {userInfo?.name || "Super Admin"}
                  </Typography>
                  <Box sx={{ textTransform: "capitalize", display: "flex", alignItems: "center", color: "#6B7280" }}>
                    <Typography variant="caption">{userInfo?.role?.toLowerCase() || "System Administrator"}</Typography>
                    <ExpandMoreIcon sx={{ color: "#6B7280", ml: 0.5, fontSize: 16 }} />
                  </Box>
                </Box>
              )}
            </Button>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              PaperProps={{
                elevation: 3,
                sx: { mt: 1.5, borderRadius: 2, minWidth: 200 }
              }}
            >
              <MenuList sx={{ py: 0 }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e5e7eb' }}>
                  <Typography variant="subtitle2" fontWeight={600} color="#111827">{userInfo?.name || "Super Admin"}</Typography>
                  <Typography variant="caption" color="textSecondary">{userInfo?.role || "System Administrator"}</Typography>
                </Box>
                <MenuItem disabled={loading} onClick={handleLogout} sx={{ color: '#ef4444', py: 1.5 }}>
                  <ListItemIcon>
                    {loading ? <CircularProgress size={20} /> : <LogoutIcon sx={{ color: '#ef4444', fontSize: 20 }} />}
                  </ListItemIcon>
                  <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                </MenuItem>
              </MenuList>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Toolbar sx={{ height: 70 }} /> {/* Spacer for fixed appbar */}
    </>
  );
}
export default Header;
